'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import dedent from 'ts-dedent';
import { ArrowsInSimple, ArrowsOutSimple } from '@phosphor-icons/react';
import { Box, CodeSnippet, CopyToClipboard, Dialog, IconButton, Stack, Tabs, Tab, Tooltip } from '@unity/cloud-ui';

interface CodeWrapperProps {
  codeBlocks: { title?: string; language: string; code: string }[];
}

export const CodeWrapper = (props: CodeWrapperProps) => {
  const { codeBlocks } = props;
  const t = useTranslations();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [selectedCodeBlockIndex, setSelectedCodeBlockIndex] = React.useState(0);

  const selectedCodeBlock = codeBlocks.length > selectedCodeBlockIndex ? codeBlocks[selectedCodeBlockIndex] : null;

  const toggleDialog = () => {
    setIsDialogOpen((prevState) => !prevState);
  };

  const handleCodeBlockChange = (_, newValue) => {
    setSelectedCodeBlockIndex(newValue);
  };

  const codeValue = (
    <CodeSnippet language={selectedCodeBlock?.language} wrapLongLines>
      {dedent(selectedCodeBlock?.code ?? '')}
    </CodeSnippet>
  );

  const shouldShowHeader = codeBlocks.length > 1 || selectedCodeBlock?.title;

  const header = shouldShowHeader ? (
    <>
      <Stack flexDirection="row" gap={0.5} justifyContent="space-between" className="code-block-header">
        <Box flex={1} width={0}>
          <Tabs
            value={selectedCodeBlockIndex}
            onChange={handleCodeBlockChange}
            sx={{ marginBottom: 0, flexGrow: 1, '.MuiTabs-scroller': { overflow: 'scroll !important' } }}
          >
            {codeBlocks.map((codeBlock, index) => (
              <Tab key={index} label={codeBlock.title} />
            ))}
          </Tabs>
        </Box>
        <Stack flexDirection="row" gap={1} marginRight={1} alignItems="center">
          <Tooltip title={t('mdx:code:code-wrapper:copy-code-to-clipboard')} placement="top">
            <CopyToClipboard value={selectedCodeBlock?.code ?? ''} />
          </Tooltip>
          <Tooltip title={t('mdx:code:code-wrapper:toggle-fullscreen')} placement="top">
            <IconButton aria-label={t('mdx:code:code-wrapper:toggle-fullscreen')} onClick={toggleDialog}>
              {isDialogOpen ? <ArrowsInSimple weight="fill" size={24} /> : <ArrowsOutSimple weight="fill" size={24} />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </>
  ) : (
    <Stack
      className="inline-code-block-controls"
      flexDirection="row"
      gap={1}
      marginRight={1}
      marginTop={1}
      position="absolute"
      right={0}
      sx={{ opacity: 0, transition: 'opacity 0.25s' }}
      alignItems="center"
    >
      <Tooltip title={t('mdx:code:code-wrapper:copy-code-to-clipboard')} placement="top">
        <CopyToClipboard value={selectedCodeBlock?.code ?? ''} />
      </Tooltip>
      <Tooltip title={t('mdx:code:code-wrapper:toggle-fullscreen')} placement="top">
        <IconButton aria-label={t('mdx:code:code-wrapper:toggle-fullscreen')} onClick={toggleDialog}>
          {isDialogOpen ? <ArrowsInSimple weight="fill" size={24} /> : <ArrowsOutSimple weight="fill" size={24} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const content = (
    <Box
      border={({ vars }) => `1px solid ${vars.palette.divider}`}
      borderRadius=".35rem"
      sx={{
        '& > pre': { margin: `0 !important` },
        position: 'relative',
        '&:hover': {
          '.inline-code-block-controls': {
            opacity: 1,
          },
        },
      }}
    >
      {header}
      {codeValue}
    </Box>
  );

  return (
    <>
      {content}
      <Dialog maxWidth="xl" open={isDialogOpen} onClose={toggleDialog}>
        {content}
      </Dialog>
    </>
  );
};

export default CodeWrapper;
